/**
 * AttorneyBondPanel: generate the attorney bond for a case (Lloyd, Aug 19 2026).
 *
 * Deliberately not a variant of AppealBondPanel, because the two differ on the thing that matters:
 *
 *  - **The amount is typed in, and nothing is computed from it.** The appeal bond swears to twice
 *    the fine, which is Lloyd's rule for appeal bonds and only those: *"Courts determine it. Varies
 *    significantly."* So there is no fine field here, no doubling, and the figure shown back after
 *    generating is the figure that was entered.
 *  - **The client signs first**, so the document is routed to the internal mailbox for a person to
 *    send for signature. It does not go to Lloyd until the client has signed.
 *
 * The sum is repeated back after generating for the same reason it is on the appeal bond: it is
 * sworn to in front of a notary, and this is the last cheap place to catch a wrong one.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BondApi, {
  AppealBondBlockedError,
  AttorneyBondResultDto,
} from '../../api/BondApi';

interface AttorneyBondPanelProps {
  Ticket_guid: string;
}

/** A base64 PDF the browser can open without the page having to host it anywhere. */
function pdfObjectUrl(base64: string): string {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
}

const AttorneyBondPanel: React.FC<AttorneyBondPanelProps> = ({ Ticket_guid }) => {
  const [amountInput, setAmountInput] = useState('');
  const [signatureDate, setSignatureDate] = useState('');
  const [showNotaryDate, setShowNotaryDate] = useState(false);
  const [notaryDate, setNotaryDate] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<AttorneyBondResultDto | null>(null);
  const [blockers, setBlockers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Revoke the previous object URL when it is replaced or the panel unmounts, or every regenerate
  // leaks a blob for the life of the page.
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    setBlockers([]);
    setResult(null);
    try {
      const dto = await BondApi.generateAttorneyBond(Ticket_guid, {
        bondAmountCents: Math.round(Number(amountInput.trim()) * 100),
        signatureDate,
        notaryDate: notaryDate || null,
      });
      setResult(dto);
      setPdfUrl(pdfObjectUrl(dto.pdfBase64));
    } catch (err) {
      if (err instanceof AppealBondBlockedError) {
        setBlockers(err.blockers.length > 0 ? err.blockers : [err.message]);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to generate the bond');
      }
    } finally {
      setGenerating(false);
    }
  };

  const amountDollars = Number(amountInput);
  const canSubmit =
    signatureDate !== '' && Number.isFinite(amountDollars) && amountDollars > 0 && !generating;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        Attorney bond
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Generates the bond for the amount the court set, and routes it for the client to sign
        first. The appeal bond&apos;s double-the-fine rule does not apply here.
      </Typography>

      {blockers.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>This case is not ready for a bond</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }}>
        <TextField
          label="Bond amount (USD)"
          type="number"
          size="small"
          value={amountInput}
          onChange={(e) => setAmountInput(e.target.value)}
          inputProps={{ min: 0.01, step: 0.01 }}
          helperText="As the court set it"
          sx={{ maxWidth: 220 }}
        />
        <TextField
          label="Signature date"
          type="date"
          size="small"
          value={signatureDate}
          onChange={(e) => setSignatureDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="The day the bond is signed"
          sx={{ maxWidth: 220 }}
        />
      </Stack>

      {showNotaryDate ? (
        <Stack sx={{ mb: 1 }}>
          <TextField
            label="Notary date"
            type="date"
            size="small"
            value={notaryDate}
            onChange={(e) => setNotaryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ maxWidth: 220 }}
          />
        </Stack>
      ) : (
        <Button size="small" onClick={() => setShowNotaryDate(true)} sx={{ mb: 1 }}>
          The bond was notarised on a different day
        </Button>
      )}

      <Box>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={!canSubmit}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {generating ? 'Generating…' : 'Generate for signature'}
        </Button>
      </Box>

      {result && (
        <Alert severity={result.deliveryError ? 'warning' : 'success'} sx={{ mt: 2 }}>
          <AlertTitle>
            {result.deliveryError
              ? 'Bond generated, but not routed'
              : `Sent to ${result.sentTo}`}
          </AlertTitle>
          <Box component="div" sx={{ mb: 1 }}>
            <div>
              Bond amount: <strong>{result.bondAmount}</strong>
            </div>
            {result.nextStep && <div>{result.nextStep}</div>}
          </Box>
          {result.deliveryError && <div style={{ marginBottom: 8 }}>{result.deliveryError}</div>}
          {pdfUrl && (
            <MuiLink href={pdfUrl} target="_blank" rel="noopener noreferrer">
              Open {result.fileName}
            </MuiLink>
          )}
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />
    </Box>
  );
};

export default AttorneyBondPanel;
